import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { X, Plus, Tag } from "lucide-react";
import { useT } from "@/lib/LanguageContext";

// Les `slug` ci-dessous sont la valeur de donnée stockée (tag existant depuis toujours
// en français dans les tenants) — seul le libellé affiché est traduit via labelKey.
const SUGGESTIONS = [
  { slug: "naturopathie", labelKey: "brand.specialty.naturopathy" },
  { slug: "acupuncture", labelKey: "brand.specialty.acupuncture" },
  { slug: "ostéopathie", labelKey: "brand.specialty.osteopathy" }, // i18n-ignore
  { slug: "coaching", labelKey: "brand.specialty.coaching" },
  { slug: "yoga", labelKey: "brand.specialty.yoga" },
  { slug: "kinésithérapie", labelKey: "brand.specialty.physiotherapy" }, // i18n-ignore
  { slug: "psychologie", labelKey: "brand.specialty.psychology" },
  { slug: "nutrition", labelKey: "brand.specialty.nutrition" },
  { slug: "homéopathie", labelKey: "brand.specialty.homeopathy" }, // i18n-ignore
  { slug: "réflexologie", labelKey: "brand.specialty.reflexology" }, // i18n-ignore
  { slug: "massothérapie", labelKey: "brand.specialty.massageTherapy" }, // i18n-ignore
  { slug: "méditation", labelKey: "brand.specialty.meditation" }, // i18n-ignore
  { slug: "aromathérapie", labelKey: "brand.specialty.aromatherapy" }, // i18n-ignore
  { slug: "phytothérapie", labelKey: "brand.specialty.herbalMedicine" }, // i18n-ignore
  { slug: "shiatsu", labelKey: "brand.specialty.shiatsu" },
];

/**
 * SpecialtiesEditor — Tag input avec suggestions cliquables.
 */
export default function SpecialtiesEditor({ value = [], onChange }) {
  const t = useT("admin");
  const [draft, setDraft] = useState("");

  const add = (val) => {
    const v = (val || draft).trim().toLowerCase();
    if (!v) return;
    if (value.map((s) => s.toLowerCase()).includes(v)) return;
    onChange([...value, v]);
    setDraft("");
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add();
    } else if (e.key === "Backspace" && !draft && value.length) {
      remove(value.length - 1);
    }
  };

  const remaining = SUGGESTIONS.filter((s) => !value.map((x) => x.toLowerCase()).includes(s.slug));

  return (
    <div data-testid="specialties-editor" className="mt-1.5 space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {value.map((s, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-900 px-2.5 py-1 rounded-full"
            data-testid={`specialty-chip-${s}`}
          >
            <Tag className="w-2.5 h-2.5" /> {s}
            <button onClick={() => remove(idx)} className="hover:text-rose-700 ml-0.5">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("brand.specialtyPlaceholder")}
          data-testid="specialty-input"
          className="text-xs h-7 w-48 inline-flex"
        />
        {draft.trim() && (
          <button
            onClick={() => add()}
            data-testid="specialty-add"
            className="inline-flex items-center text-xs text-emerald-700 hover:text-emerald-900 ml-1"
          >
            <Plus className="w-3 h-3" /> {t("brand.add")}
          </button>
        )}
      </div>
      {remaining.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-eh-muted mb-1">{t("brand.suggestions")}</div>
          <div className="flex flex-wrap gap-1.5">
            {remaining.slice(0, 10).map((s) => (
              <button
                key={s.slug}
                onClick={() => add(s.slug)}
                data-testid={`specialty-suggest-${s.slug}`}
                className="text-[10px] px-2 py-0.5 rounded-full bg-eh-cream/50 text-eh-muted hover:bg-amber-100 hover:text-amber-900 transition"
              >
                + {t(s.labelKey)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
